// src/components/operationCenter/widgets/GenericEndpointBarChartWidget.tsx
// Ranked/categorical bar chart from any backend endpoint.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps, GenericEndpointBarChartConfig } from "@/types/dashboard";
import type { ValueFormat } from "./shared/genericWidgetUtils";
import {
  fetchEndpoint,
  extractRecords,
  parseParams,
  paramsToString,
  formatValue,
} from "./shared/genericWidgetUtils";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  CHART_COLORS,
  createTooltip,
  createGrid,
  createXAxis,
  createYAxis,
} from "@/utils/charts/theme";
import { WIDGET_EMPTY } from "../WidgetWrapper";

const FORMAT_OPTIONS: ValueFormat[] = ["number", "currency", "compact", "percent", "ratio"];

function buildBarOption(
  records: Record<string, unknown>[],
  labelField: string,
  valueField: string,
  format: ValueFormat,
  horizontal: boolean,
  colorByValue: boolean,
): EChartsOption {
  const labels = records.map((r) => String(r[labelField] ?? ""));
  const values = records.map((r) => {
    const v = r[valueField];
    return typeof v === "number" ? v : parseFloat(String(v));
  });

  const itemStyle = colorByValue
    ? {
        color: (params: { value: number }) =>
          params.value >= 0 ? CHART_COLORS.semantic.positive : CHART_COLORS.semantic.negative,
      }
    : { color: CHART_COLORS.series[0] ?? CHART_COLORS.text.primary };

  const yFormatter = (v: number) => formatValue(v, format);

  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: createTooltip({
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: unknown) => {
        const p = params as { name: string; value: number }[];
        if (!p?.length) return "";
        return `${p[0]!.name}<br/>${formatValue(p[0]!.value, format)}`;
      },
    }),
    grid: createGrid({ left: horizontal ? 100 : 56, right: 16, bottom: 24, top: 16 }),
    xAxis: horizontal
      ? createYAxis({ formatter: yFormatter }) as unknown as ReturnType<typeof createXAxis>
      : createXAxis({ data: labels, rotate: labels.length > 8 ? 20 : 0, boundaryGap: true }),
    yAxis: horizontal
      ? createXAxis({ data: labels, boundaryGap: true }) as unknown as ReturnType<typeof createYAxis>
      : createYAxis({ formatter: yFormatter }),
    series: [
      {
        type: "bar" as const,
        data: values,
        itemStyle,
        label: {
          show: records.length <= 20,
          position: horizontal ? "right" : "top",
          formatter: (params: { value: number }) => formatValue(params.value, format),
          fontSize: 10,
          color: CHART_COLORS.text.muted,
        },
      },
    ],
  } as EChartsOption;
}

const GenericEndpointBarChartWidget: React.FC<WidgetProps<GenericEndpointBarChartConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericEndpointBarChartConfig;

  const [showConfig, setShowConfig] = useState(!config.endpoint);
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Bar Chart");
  const [draftLabelField, setDraftLabelField] = useState(config.label_field ?? "name");
  const [draftValueField, setDraftValueField] = useState(config.value_field ?? "value");
  const [draftFormat, setDraftFormat] = useState<ValueFormat>(config.format ?? "number");
  const [draftSort, setDraftSort] = useState(config.sort ?? "desc");
  const [draftLimit, setDraftLimit] = useState(String(config.limit ?? 20));
  const [draftHorizontal, setDraftHorizontal] = useState(config.horizontal ?? false);
  const [draftColorByValue, setDraftColorByValue] = useState(config.color_by_value ?? false);
  const [draftParams, setDraftParams] = useState(paramsToString(config.params));

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(250);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const h = containerRef.current.clientHeight;
        if (h > 0) setChartHeight(h);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Bar Chart");
    setDraftLabelField(config.label_field ?? "name");
    setDraftValueField(config.value_field ?? "value");
    setDraftFormat(config.format ?? "number");
    setDraftSort(config.sort ?? "desc");
    setDraftLimit(String(config.limit ?? 20));
    setDraftHorizontal(config.horizontal ?? false);
    setDraftColorByValue(config.color_by_value ?? false);
    setDraftParams(paramsToString(config.params));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["generic_endpoint_bar_chart", config.endpoint, config.params],
    queryFn: ({ signal }) => fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 60_000,
    retry: 1,
  });

  const { chartOption, hasData } = useMemo(() => {
    if (!rawData) return { chartOption: {} as EChartsOption, hasData: false };
    let records = extractRecords(rawData);
    if (!records.length) return { chartOption: {} as EChartsOption, hasData: false };

    const vf = config.value_field ?? "value";
    if (config.sort !== "none") {
      records = [...records].sort((a, b) => {
        const av = Number(a[vf] ?? 0);
        const bv = Number(b[vf] ?? 0);
        return config.sort === "asc" ? av - bv : bv - av;
      });
    }
    if (config.limit) records = records.slice(0, config.limit);

    return {
      chartOption: buildBarOption(
        records,
        config.label_field ?? "name",
        vf,
        config.format ?? "number",
        config.horizontal ?? false,
        config.color_by_value ?? false,
      ),
      hasData: true,
    };
  }, [rawData, config]);

  const handleSave = useCallback(() => {
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      label_field: draftLabelField,
      value_field: draftValueField,
      format: draftFormat,
      sort: draftSort as "asc" | "desc" | "none",
      limit: parseInt(draftLimit, 10) || 20,
      horizontal: draftHorizontal,
      color_by_value: draftColorByValue,
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [draftEndpoint, draftTitle, draftLabelField, draftValueField, draftFormat, draftSort,
      draftLimit, draftHorizontal, draftColorByValue, draftParams, onConfigChange]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Bar Chart");
    setDraftLabelField(config.label_field ?? "name");
    setDraftValueField(config.value_field ?? "value");
    setDraftFormat(config.format ?? "number");
    setDraftSort(config.sort ?? "desc");
    setDraftLimit(String(config.limit ?? 20));
    setDraftHorizontal(config.horizontal ?? false);
    setDraftColorByValue(config.color_by_value ?? false);
    setDraftParams(paramsToString(config.params));
    setShowConfig(false);
  }, [config]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Bar Chart"}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => refetch()} disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Refresh">
            <RefreshCw size={12} />
          </button>
          <button onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Configure">
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-64">
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Endpoint</label>
              <input type="text" value={draftEndpoint} onChange={(e) => setDraftEndpoint(e.target.value)}
                placeholder="/api/v1/movers" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Title</label>
              <input type="text" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Bar Chart" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Label Field</label>
              <input type="text" value={draftLabelField} onChange={(e) => setDraftLabelField(e.target.value)}
                placeholder="name" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Value Field</label>
              <input type="text" value={draftValueField} onChange={(e) => setDraftValueField(e.target.value)}
                placeholder="value" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Format</label>
              <select value={draftFormat} onChange={(e) => setDraftFormat(e.target.value as ValueFormat)}
                className="select select-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5">
                {FORMAT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Sort</label>
              <select value={draftSort} onChange={(e) => setDraftSort(e.target.value as "asc" | "desc" | "none")}
                className="select select-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5">
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Limit</label>
              <input type="number" value={draftLimit} onChange={(e) => setDraftLimit(e.target.value)}
                min={1} max={100} className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[100px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Query Params</label>
              <input type="text" value={draftParams} onChange={(e) => setDraftParams(e.target.value)}
                placeholder="key=val" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={draftHorizontal} onChange={(e) => setDraftHorizontal(e.target.checked)}
                className="checkbox checkbox-xs" />
              <span className="text-[11px] text-slate-300">Horizontal</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={draftColorByValue} onChange={(e) => setDraftColorByValue(e.target.checked)}
                className="checkbox checkbox-xs" />
              <span className="text-[11px] text-slate-300">Color by value</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={handleCancel} className="btn btn-xs btn-ghost text-slate-400">Cancel</button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">Apply</button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div ref={containerRef} className="flex-1 min-h-0">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-xs btn-outline btn-primary">Configure</button>
            </div>
          </div>
        ) : (
          <EChartsRenderer
            chartOption={chartOption}
            height={chartHeight}
            isLoading={isLoading}
            error={error instanceof Error ? error.message : (error ? String(error) : null)}
            hasData={hasData}
            containerClassName=""
          />
        )}
      </div>
    </div>
  );
};

export default GenericEndpointBarChartWidget;
